import React from 'react';
import {FormikContextType, FormikHelpers} from "formik";
import Form, {
  FormBlock,
  FormSection,
  MediaField,
  NumberField,
  SelectField,
  TextAreaField,
  TextField,
  ToggleField
} from 'components/Form/Form';
import {Achievement, AchievementData} from "types/Achievement";
import {AchievementType} from "types/AchievementType";
import {InformationCircleIcon} from "@heroicons/react/outline";
import {toTitleCase} from "../../../services/string";
import {useOperator} from "../../../context/OperatorContext";
import IconName from "../../../types/IconName";
import useGetTrails from "../../../hooks/api/useGetTrails";
import useGetQuizzes from "../../../hooks/api/useGetQuizzes";
import {Quiz} from "../../../types/Quiz";
import {TrailPreview} from "../../../types/Trail";
import Toggle from "../../../components/Form/Toggle";
import Alert from "../../../components/Alert";
import * as Yup from "yup";

// Constants ----

const defaultCriteria = {
  count: undefined,
  score: undefined,
  unique: undefined,
};

// Types ----

type AchievementFormProps = {
  achievement?: Achievement | null
  onSubmit: (values: AchievementData, helpers: FormikHelpers<AchievementData>) => any
}

// Helpers ----

const typeOptions = Object
  .values(AchievementType)
  .filter(type => type !== AchievementType.UNKNOWN)
  .map(type => ({
    id: type,
    name: toTitleCase(type),
  }));

const trailOptions = (trails: Array<TrailPreview>) => trails.map(trail => ({
  id: trail.id,
  name: `${toTitleCase(trail.name)}`,
  icon: IconName.QUESTION_MARK_CIRCLE
}))

const quizOptions = (quizzes: Array<Quiz>) => quizzes.map(quiz => ({
  id: quiz.id,
  name: `${toTitleCase(quiz.title)}`,
  icon: IconName.MAP
}))

// Determine subject options
function getSubjectOptions(type: AchievementType, trails: Array<TrailPreview>, quizzes: Array<Quiz>) {
  switch (type) {
    case AchievementType.TRAIL_COMPLETE:
      return trailOptions(trails);
    case AchievementType.QUIZ_COMPLETE:
      return quizOptions(quizzes);
    default:
      return [];
  }
}

// Handle type change
function handleChangeType(form: FormikContextType<AchievementData>, type: AchievementType) {
  if (!type) return;

  void form.setFieldValue('unlock_criteria', defaultCriteria);

  // Clear subject fields if type changes
  if (form.values.type !== type) {
    void form.setFieldValue('subject_type', null);
    void form.setFieldValue('subject_id', null);
  }
}

// Handle subject type change
function handleChangeSubject(form: FormikContextType<AchievementData>) {
  void form.setFieldValue('subject_type', getTypeName(form.values.type ?? AchievementType.UNKNOWN)).then(_ => {
    void form.validateField('subject_type');
  });
}

// Handle toggle any subject
function handleToggleAnySubject(checked: boolean, form: FormikContextType<AchievementData>) {
  void form.setFieldValue('any_subject', checked).then(_ => {
    void form.validateField('unlock_criteria');
  });
  void form.setFieldValue('subject_type', null);
  void form.setFieldValue('subject_id', null);

  // Clear unique criteria value if toggled off in case it was set while toggled on
  if (!checked) void form.setFieldValue('unlock_criteria.unique', undefined);
}

// Get type name
function getTypeName(type: AchievementType) {
  switch (type) {
    case AchievementType.TRAIL_COMPLETE:
      return 'trail';
    case AchievementType.QUIZ_COMPLETE:
      return 'quiz';
    default:
      return '';
  }
}

// Component ----

function AchievementForm({achievement, onSubmit}: AchievementFormProps) {
  const {termFor} = useOperator();

  const {data: trails, isLoading: trailsLoading} = useGetTrails({page: 1, perPage: 100});
  const {data: quizzes, isLoading: quizzesLoading} = useGetQuizzes({page: 1, perPage: 200});

  const isLocked: boolean = !!achievement?.progressing_user_count;

  const validationSchema = Yup.object().shape({
    type: Yup.string().notOneOf([AchievementType.UNKNOWN], 'This field is required').required("This field is required"),
    name: Yup.string().required("This field is required").max(250, 'Maximum of 250 characters'),
    subject_type: Yup.string()
      .when(['any_subject'], ([anySubject]: any, schema: any) => {
        return anySubject ? schema.nullable() : schema.required("This field is required");
      }),
    subject_id: Yup.number()
      .when(['any_subject'], ([anySubject]: any, schema: any) => {
        return anySubject ? schema.nullable() : schema.required("This field is required");
      }),
    points: Yup.number()
      .integer('Must be a whole number (no decimals allowed)')
      .min(0, 'Must be at least 0')
      .max(1_000_000, "Can't be more than 1 million (1,000,000)")
      .required("This field is required"),
    description: Yup.string().required("This field is required").max(250, 'Maximum character limit exceeded'),
    unlock_criteria: Yup.object()
      .when(['type'], ([type]: any, schema: any) => {
        switch (type) {
          case AchievementType.TRAIL_COMPLETE:
            return schema.shape({
              count: Yup.number()
                .min(1, 'Must be at least 1')
                .required("This field is required"),
              unique: Yup.boolean().optional()
            })
          case AchievementType.QUIZ_COMPLETE:
            return schema.shape({
              count: Yup.number()
                .min(1, 'Must be at least 1')
                .required("This field is required"),
              score: Yup.number()
                .min(0, 'Must be at least 0')
                .max(100, 'Must be a maximum of 100')
                .required("This field is required"),
              unique: Yup.boolean().optional()
            })
        }
      }),
    hasSubject: Yup.boolean(),
  })

  const initialValues: AchievementData = {
    type: achievement?.type ?? AchievementType.UNKNOWN,
    name: achievement?.name ?? '',
    description: achievement?.description ?? '',
    is_secret: achievement?.is_secret ?? false,
    points: achievement?.points ?? 0,
    image_id: achievement?.image?.id ?? null,
    unlock_criteria: achievement?.unlock_criteria ?? defaultCriteria,
    subject_type: achievement?.subject_type ?? null,
    subject_id: achievement?.subject?.id ?? null,
    any_subject: !achievement?.subject?.id ?? true,
  }

  return (
    <Form initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      <FormSection
        name='Key Information'
        description={`Basic details about the ${termFor('achievement')}`}
        columns={6}
      >

        {(form) => {
          return (
            <>
                {isLocked &&
                  <FormBlock columns={6}>
                    <Alert message={`Users have started making progress on this ${termFor('achievement')}, so some options have been locked.`} />
                  </FormBlock>
                }

                {/* Type */}
                <FormBlock columns={3}>
                  <SelectField
                    name="type"
                    label="Type *"
                    help="Determines which unlock criteria are available"
                    options={typeOptions}
                    autoComplete="off"
                    onChange={(value: any) => handleChangeType(form, value?.id)}
                    disabled={isLocked}
                  />
                </FormBlock>

              {/* Name */}
              <FormBlock columns={3}>
                <TextField
                  name="name"
                  label="Name *"
                  hint="250 chars max"
                  help="The publically visible display name"
                  autoComplete="off"
                  maxLength={250}
                  autoFocus
                />
              </FormBlock>

              {/* Subject toggle */}
              <FormBlock columns={6}>
                <Toggle
                  label={`Any ${getTypeName(form.values.type)}`}
                  checked={form.values.any_subject}
                  onChange={checked => handleToggleAnySubject(checked, form)}
                  disabled={form.values.type === AchievementType.UNKNOWN || isLocked}
                />
              </FormBlock>

              {/* Subject */}
              {!form.values.any_subject &&
                <>
                  <FormBlock columns={3}>
                    <SelectField
                      name="subject_id"
                      label={`${toTitleCase(getTypeName(form.values.type) || 'Entity')} *`}
                      help={form.values.type === AchievementType.UNKNOWN ? 'You must choose a type first' : undefined}
                      options={getSubjectOptions(form.values.type, trails?.data ?? [], quizzes?.data ?? [])}
                      autoComplete="off"
                      onChange={() => handleChangeSubject(form)}
                      disabled={form.values.type === AchievementType.UNKNOWN || isLocked}
                      isLoading={trailsLoading || quizzesLoading}
                    />
                  </FormBlock>
                  <FormBlock columns={2}/>
                </>
              }

              {/* Points */}
              <FormBlock columns={3}>
                <NumberField
                  name="points"
                  label="Points *"
                  autoComplete="off"
                />
              </FormBlock>

              {/* Description */}
              <FormBlock columns={6}>
                <TextAreaField
                  name="description"
                  label="Description *"
                  hint="250 chars max"
                  help={`Brief descriptive information about how to unlock this ${termFor('achievement')}`}
                  autoComplete="off"
                  rows={3}
                  maxChars={250}
                />
              </FormBlock>

              {/* Image */}
              <FormBlock columns={6}>
                <MediaField
                  name="image_id"
                  label="Image"
                  hint="Optional"
                  files={achievement?.image}
                  maxFiles={1}
                  type={MediaField.types.IMAGE}
                />
              </FormBlock>

              {/* Secret */}
              <FormBlock columns={6}>
                <ToggleField
                  name="is_secret"
                  label="Secret"
                  description={`Hide this ${termFor('achievement')} until it is unlocked`}
                />
              </FormBlock>
            </>

          )
        }}
      </FormSection>

      <FormSection
        name="Unlock Criteria"
        description={`Configure how this ${termFor('achievement')} is unlocked`}
        columns={6}
      >
        {form =>
          <>
            {/* Unlock Criteria */}
            <FormBlock columns={6}>
              {(!form.values.type || form.values.type === AchievementType.UNKNOWN) &&
                <div className="text-center sm:col-span-6">
                  <InformationCircleIcon className="mx-auto h-10 w-10 text-gray-400"/>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    {`${termFor('Achievement')} type not selected`}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {`Get started by selecting the ${termFor('achievement')} type`}
                  </p>
                </div>
              }

              {form.values.type === AchievementType.TRAIL_COMPLETE &&
                <div className="md:grid md:grid-cols-3 md:gap-4">
                  <NumberField
                    name="unlock_criteria.count"
                    label="Count *"
                    disabled={isLocked}
                  />

                  {form.values.any_subject &&
                    <div className="md:mt-6">
                      <ToggleField
                        name="unlock_criteria.unique"
                        label="Unique"
                        description="Each trail only counts once"
                        disabled={isLocked}
                      />
                    </div>
                  }
                </div>
              }

              {form.values.type === AchievementType.QUIZ_COMPLETE &&
                <div className="md:grid md:grid-cols-3 md:gap-4">
                  <NumberField
                    name="unlock_criteria.count"
                    label="Count *"
                    disabled={isLocked}
                  />

                  <NumberField
                    name="unlock_criteria.score"
                    label="Score (%) *"
                    disabled={isLocked}
                  />

                  {form.values.any_subject &&
                    <div className="md:mt-6">
                      <ToggleField
                        name="unlock_criteria.unique"
                        label="Unique"
                        description="Each quiz only counts once"
                        disabled={isLocked}
                      />
                    </div>
                  }
                </div>
              }
            </FormBlock>
          </>
        }
      </FormSection>
    </Form>
  );
}

export default AchievementForm;